export const DIALOG_SIZES = ["confirm", "small", "medium", "large", "xlarge", "full"] as const;
export const DIALOG_HEIGHTS = ["hug", "tall"] as const;

export type DialogSize = typeof DIALOG_SIZES[number];
/**
 * Right padding that keeps a header clear of the host close: the 44px close, its 12px inset and an 8px gap, so 64px
 * from the dialog edge. A paddingless dialog's header brings its own gutter and reserves all of it; otherwise the
 * dialog's 20px gutter already covers part of it.
 */
export function closeReserveClass(paddingless: boolean): string {
  return paddingless ? "pr-16" : "pr-11";
}
export type DialogHeight = typeof DIALOG_HEIGHTS[number];

const SIZE_ALIASES: Record<string, DialogSize> = {
  "default": "medium",
  "normal": "medium",
  "auto": "medium",
  "none": "medium",
  "extra-large": "xlarge",
  "xl": "xlarge",
  "task": "full",
  "client": "full",
  "fullscreen": "full",
  "drawer": "full",
};

export function isDialogSize(value: string): value is DialogSize {
  return (DIALOG_SIZES as readonly string[]).includes(value);
}

export function resolveDialogSize(size?: string | null): DialogSize {
  if (!size) {
    return "medium";
  }
  if (isDialogSize(size)) {
    return size;
  }
  return SIZE_ALIASES[size] ?? "medium";
}

export function isDialogHeight(value: string): value is DialogHeight {
  return (DIALOG_HEIGHTS as readonly string[]).includes(value);
}

export function resolveDialogHeight(height?: string | null): DialogHeight {
  if (!height) {
    return "hug";
  }
  if (isDialogHeight(height)) {
    return height;
  }
  return "hug";
}

/**
 * An exact dialog width in px, for designs that need a width between the size steps. It still shrinks to the
 * viewport, and on phones the size's own sheet behaviour wins. `undefined` keeps the width of the size.
 */
export function resolveDialogWidth(width?: number | null): number | undefined {
  if (width === undefined || width === null) {
    return undefined;
  }
  if (!Number.isFinite(width) || width <= 0) {
    throw new RangeError(`Dialog width must be a positive number of pixels, got ${String(width)}.`);
  }
  return width;
}
